import clone from 'lodash/clone';
import { config } from 'config';
import { createFormData } from 'hooks';
import i18n from 'i18nextInstance';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import orderBy from 'lodash/orderBy';
import patientServices from './services';
import { push } from 'react-router-redux';
import {
    call,
    put,
    select,
    takeEvery
} from 'redux-saga/effects';
import {
    clearObject,
    uppercaseFirst
} from 'shared/utils';
import {
    parseAbsenceReport,
    parseAbsenceReports,
    parseAudit,
    parseImportErrors,
    parsePatients,
    patientActions
} from './';
const { doctorsRelated } = config;

function* _attachPatient ({ payload: { patient_id, ...payload } }) {
    try {
        yield put(patientActions.setPatientAttachStatus({ loading: true }));
        yield call(patientServices.putAttachPatient, { patient_id }, payload);
        yield put(patientActions.setPatientAttachStatus({ success: true }));
    } catch (e) {
        const parseError = e => {
            switch (e?.response?.data?.messages) {
            case ('PATIENT_ALREADY_RELATED'): return i18n.t('El paciente ya está asociado al profesional');
            default: return i18n.t('Ocurrió un error al asociar el paciente');
            }
        };
        yield put(patientActions.setPatientAttachStatus({ error: parseError(e) }));
    }
}

function* _createPatient ({ payload }) {
    try {
        yield put(patientActions.setPatientCreation('loading'));
        yield put(patientActions.setPatientRegistrationError({}));
        yield put(patientActions.setPatientLoading(true));
        yield call(patientServices.postPatient, null, payload);
        yield put(patientActions.setPatientCreation(''));
        yield put(push(i18n.t('/pacientes/listar')));
    } catch (e) {
        yield put(patientActions.setPatientCreation('error'));
        const { account: { user: { restricted_access: restrictedAccesss } } } = yield select();
        const errors = e?.response?.data;
        const doctorsRelatedError = !doctorsRelated ? '' : `. ${ i18n.t('Debe completar correctamente el apellido y la fecha de nacimiento.') }`;
        if (!!errors) {
            yield put(patientActions.setPatientRegistrationError({
                ...(errors?.identification && ['IDENTIFICATION_ALREADY_IN_USE'].includes(errors.identification[0])
                    && { identification: `${ i18n.t('El DNI ya fue utlizado') }${ doctorsRelatedError }` }),
                ...(errors?.email && errors.email[0] === 'SHOULD_BE_EMAIL'
                    && { email: i18n.t('Email inválido') }),
                ...(!!restrictedAccesss && errors?.patient && errors.patient[0] === 'ALREADY_EXISTS_FOR_YOUR_PROFESSIONALS'
                    && { identification: i18n.t('El paciente ya se encuentra registrado para alguno de tus profesionales') }),
                ...(errors?.messages === 'PATIENT_WILL_NOT_BE_RELATED'
                    && { general: i18n.t('No puedes crear el paciente porque no estará asociado a tu perfil. Por favor incorpora más datos de asociación.') })
            }));
        }
    } finally {
        yield put(patientActions.setPatientLoading(false));
    }
}

function* _createPatientExternalLogin ({ payload }) {
    try {
        yield put(patientActions.setPatientLoading(true));
        const { data: { redirect } } = yield call(patientServices.postPatientExternalLogin, null, payload);
        yield put(patientActions.setPatientExternalLogin(redirect));
    } catch (e) {

    } finally {
        yield put(patientActions.setPatientLoading(false));
    }
}

function* _deletePatient ({ payload }) {
    try {
        yield put(patientActions.setPatientLoading(true));
        yield put(patientActions.setPatientDeleteError(''));
        yield call(patientServices.deletePatient, { patient_id: payload });
        const { patient: { list } } = yield select();
        yield put(patientActions.setPatientList(list.filter(({ id }) => id !== payload)));
        yield put(patientActions.setPatientDeleteSuccess(true));
        yield put(push(i18n.t('/pacientes/listar')));
    } catch (e) {
        yield put(patientActions.setPatientDeleteError(i18n.t('No fue posible eliminar el paciente, es posible que tenga actividad en la plataforma.')));
    } finally {
        yield put(patientActions.setPatientLoading(false));
    }
}

function* _getAbsenceReport ({ payload }) {
    try {
        yield put(patientActions.setPatientAbsenceReportLoading(true));
        const { data } = yield call(patientServices.fetchAbsenceReport, { id: payload });
        yield put(patientActions.setPatientAbsenceReport(parseAbsenceReport(data)));
    } catch (e) {

    } finally {
        yield put(patientActions.setPatientAbsenceReportLoading(false));
    }
}

function* _getAbsenceReportsPage ({ payload }) {
    try {
        yield put(patientActions.setPatientAbsenceReportsPageLoading(true));
        const { data } = yield call(patientServices.fetchAbsenceReportsPage, null, payload);
        yield put(patientActions.setPatientAbsenceReportsPage({
            ...data,
            filters: payload
        }));
    } catch (e) {

    } finally {
        yield put(patientActions.setPatientAbsenceReportsPageLoading(false));
    }
}

function* _getAbsenceReportsDownload ({ payload }) {
    try {
        yield put(patientActions.setPatientAbsenceReportsDownloading(true));
        const { data } = yield call(patientServices.fetchAbsenceReportsDownload, null, payload);
        yield put(patientActions.setPatientAbsenceReportsDownload(parseAbsenceReports(data)));
    } catch (e) {

    } finally {
        yield put(patientActions.setPatientAbsenceReportsDownloading(false));
    }
}

function* _getPatientAudit ({ payload }) {
    try {
        payload.limit = 20;
        yield put(patientActions.setPatientLoading(true));
        const { action, ...params } = payload;
        const newFilters = {
            ...params,
            ...(!!action && { action })
        };
        const { data } = yield call(patientServices.fetchPatientAudit, null, newFilters);
        yield put(patientActions.setPatientAudit({
            ...data,
            filters: newFilters
        }));
    } catch (e) {

    } finally {
        yield put(patientActions.setPatientLoading(false));
    }
}

function* _getPatientAuditDownload ({ payload }) {
    try {
        yield put(patientActions.setPatientAuditDownloading(true));
        const { action, ...params } = payload;
        const newFilters = {
            one_page: 1,
            ...params,
            ...(!!action && { action })
        };
        const { data: { data } } = yield call(patientServices.fetchPatientAudit, null, newFilters);
        yield put(patientActions.setPatientAuditDownload(parseAudit(data)));
    } catch (e) {

    } finally {
        yield put(patientActions.setPatientAuditDownloading(false));
    }
}

function* _getPatientData ({ payload }) {
    try {
        yield put(patientActions.setPatientLoading(true));
        yield put(patientActions.setPatientSelected({}));
        const { data } = yield call(patientServices.fetchPatientData, { patient_id: payload });
        yield put(patientActions.setPatientSelected(data));
    } catch (e) {

    } finally {
        yield put(patientActions.setPatientLoading(false));
    }
}

function* _getPatientHistory ({ payload }) {
    try {
        const parseCalls = list => list.map(({
            call_doctors,
            created_at,
            diagnosis,
            duration,
            guests,
            images,
            note,
            presumptive_diagnosis,
            recommendations,
            symptoms
        }) => ({
            call_doctors: orderBy(call_doctors, ['call_update'], ['desc']),
            created_at: created_at ? moment(created_at).format('YYYY-MM-DD HH:mm') : '',
            date: created_at ? moment(created_at).format('YYYY-MM-DD ') : '',
            diagnosis,
            duration,
            guests,
            hour: created_at ? moment(created_at).format('HH:mm') : '',
            images,
            note,
            presumptive_diagnosis,
            recommendations,
            symptoms,
            type: 'call'
        }));
        const parseDiagnostics = list => list.map(({
            ...diagnostic
        }) => ({
            ...diagnostic,
            type: 'history'
        }));
        yield put(patientActions.setPatientLoading(true));
        const { data: { calls, diagnostics, doctors_related = [], ...data } } = yield call(patientServices.fetchPatientHistory, null, { patient_id: payload });
        yield put(patientActions.setPatientData({
            calls: orderBy([...parseCalls(calls), ...parseDiagnostics(diagnostics)], ['created_at'], ['desc']),
            doctors_related: orderBy(doctors_related.map(({ first_name, last_name }) => ({ label: `${ last_name }, ${ first_name }` })), ['label']),
            ...data
        }));
    } catch (e) {
        
    } finally {
        yield put(patientActions.setPatientLoading(false));
    }
}

function* _getPatientPage ({ payload }) {
    try {
        yield put(patientActions.setPatientPageLoading(true));
        const body = { limit: 15, ...payload };
        const { data: pageData } = yield call(patientServices.fetchPatientPage, null, body);
        yield put(patientActions.setPatientPage({
            ...pageData,
            filters: body
        }));
    } catch (e) {
        
    } finally {
        yield put(patientActions.setPatientPageLoading(false));
    }
}

function* _getPatientPageDownload () {
    try {
        yield put(patientActions.setPatientPageDownloading(true));
        const { data: pageData } = yield call(patientServices.fetchPatientPage, null, { one_page: 1 });
        const { data } = pageData ?? {};
        yield put(patientActions.setPatientPageDownload(parsePatients(data)));
    } catch (e) {
        
    } finally {
        yield put(patientActions.setPatientPageDownloading(false));
    }
}

function* _getPatientPositions ({ payload: patient_id }) {
    try {
        yield put(patientActions.setPatientPositionsLoading(true));
        const { data } = yield call(patientServices.fetchPatientPositions, { patient_id }, null);
        yield put(patientActions.setPatientPositions(data));
    } catch (e) {

    } finally {
        yield put(patientActions.setPatientPositionsLoading(false));
    }
}

function* _getPatientRecap ({ payload }) {
    try {
        yield put(patientActions.setPatientRecapLoading(true));
        const { id, type } = payload;
        const { data } = yield call(type === 'call' ? patientServices.fetchPatientRecap : patientServices.fetchPatientDiagnostic, { id });
        if (type === 'evolution') {
            data.call_doctors = [data.call_doctors];
        }
        yield put(patientActions.setPatientRecap({
            ...data,
            type: uppercaseFirst(type)
        }));
    } catch (e) {
        
    } finally {
        yield put(patientActions.setPatientRecapLoading(false));
    }
}

function* _getPatientSingle ({ payload }) {
    try {
        yield put(patientActions.setPatientSingleLoading(true));
        const { data = [] } = yield call(patientServices.getPatientList, null, payload);
        yield put(patientActions.setPatientSingle(data[0]));
    } catch (e) {
        
    } finally {
        yield put(patientActions.setPatientSingleLoading(false));
    }
}

function* _handleImportPatientsFinished ({ payload }) {
    try {
        const { errors = [], succeded = 0, updated = 0 } = payload;
        yield put(patientActions.setPatientImportErrors(parseImportErrors(errors)));
        yield put(patientActions.setPatientImportSucceded({ succeded, updated }));
        yield put(patientActions.setPatientImportLoading(false));
    } catch (e) {

    }
}

function* _handleImportPatientsUpdated ({ payload }) {
    try {
        yield put(patientActions.setPatientImportInfo(payload));
    } catch (e) {

    }
}

function* _importPatients ({ payload }) {
    try {
        const { company_id, file, office_id } = payload;
        yield put(patientActions.setPatientImportUploading(true));
        yield put(patientActions.setPatientImportLoading(true));
        yield put(patientActions.setPatientImportGlobalError(''));
        yield put(patientActions.setPatientImportErrors([]));
        yield put(patientActions.setPatientImportSucceded({}));
        yield put(patientActions.setPatientImportInfo({}));
        const body = createFormData(clearObject({
            company_id,
            file,
            office_id
        }));
        yield call(patientServices.postPatientImport, null, body);
    } catch (e) {
        const { file } = e?.response?.data ?? {};
        if (file && file[0] === 'FORMAT_INVALID') {
            yield put(patientActions.setPatientImportGlobalError(i18n.t('La extensión del archivo no es válida')));
        } else {
            yield put(patientActions.setPatientImportGlobalError(i18n.t('Ocurrió un error al importar el archivo')));
        }
        yield put(patientActions.setPatientImportLoading(false));
    } finally {
        yield put(patientActions.setPatientImportUploading(false));
    }
}

function* _pinRegistration ({ payload }) {
    try {
        const { data: { pin } } = yield call(patientServices.registerPatient, null, payload);
        yield put(patientActions.setPatientPin(pin.toString()));
    } catch (e) {}
}

function* _sendPatientSocket ({ payload }) {
    try {
        yield call(patientServices.postPatientSocket, { patient_id: payload });
    } catch (e) {}
}

function* _updatePatient ({ payload }) {
    try {
        yield put(patientActions.setPatientLoading(true));
        yield put(patientActions.setPatientRegistrationError({}));
        const { data: { first_name, identification, last_name, patient_id } } = yield call(patientServices.putPatient, null, payload);
        const { patient: { list } } = yield select();
        const existingPatient = list.findIndex(({ id }) => id === patient_id);
        if (existingPatient >= 0) {
            const newList = clone(list);
            newList[existingPatient] = {
                ...newList[existingPatient],
                first_name,
                identification,
                last_name
            };
            yield put(patientActions.setPatientList(newList));
        }
        yield put(patientActions.setPatientUpdateSuccess(true));
        yield put(push(`${ i18n.t('/pacientes/historial') }/${ patient_id }`));
    } catch ({ response: { data } }) {
        if (!isEmpty(data.identification)) {
            yield put(patientActions.setPatientRegistrationError({ identification: i18n.t('El documento ya se encuentra utilizado.') }));
        }
    } finally {
        yield put(patientActions.setPatientLoading(false));
    }
}

function* _updatePatientPositions ({ payload }) {
    try {
        yield put(patientActions.setPatientPositionsLoading(true));
        const { patient_id, ...body } = payload;
        yield call(patientServices.putPatientPositions, { patient_id }, body);
        yield put(patientActions.setPatientPositionsSucceded(true));
    } catch (e) {

    } finally {
        yield put(patientActions.setPatientPositionsLoading(false));
    }
}

export default [
    takeEvery(patientActions.attachPatient.type, _attachPatient),
    takeEvery(patientActions.createPatient.type, _createPatient),
    takeEvery(patientActions.createPatientExternalLogin.type, _createPatientExternalLogin),
    takeEvery(patientActions.deletePatient.type, _deletePatient),
    takeEvery(patientActions.getAbsenceReport.type, _getAbsenceReport),
    takeEvery(patientActions.getAbsenceReportsPage.type, _getAbsenceReportsPage),
    takeEvery(patientActions.getAbsenceReportsDownload.type, _getAbsenceReportsDownload),
    takeEvery(patientActions.getPatientAudit.type, _getPatientAudit),
    takeEvery(patientActions.getPatientAuditDownload.type, _getPatientAuditDownload),
    takeEvery(patientActions.getPatientData.type, _getPatientData),
    takeEvery(patientActions.getPatientHistory.type, _getPatientHistory),
    takeEvery(patientActions.getPatientPage.type, _getPatientPage),
    takeEvery(patientActions.getPatientPageDownload.type, _getPatientPageDownload),
    takeEvery(patientActions.getPatientPositions.type, _getPatientPositions),
    takeEvery(patientActions.getPatientRecap.type, _getPatientRecap),
    takeEvery(patientActions.getPatientSingle.type, _getPatientSingle),
    takeEvery(patientActions.handleImportPatientsFinished.type, _handleImportPatientsFinished),
    takeEvery(patientActions.handleImportPatientsUpdated.type, _handleImportPatientsUpdated),
    takeEvery(patientActions.importPatients.type, _importPatients),
    takeEvery(patientActions.pinRegistration.type, _pinRegistration),
    takeEvery(patientActions.sendPatientSocket.type, _sendPatientSocket),
    takeEvery(patientActions.updatePatient.type, _updatePatient),
    takeEvery(patientActions.updatePatientPositions.type, _updatePatientPositions)
];
